import React, { useState, useMemo } from "react";
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";

import {
  Collapse,
  Image,
  Divider,
  Button
} from '@lockerpm/design';

import {
  ShareAltOutlined,
  ThunderboltOutlined,
  PlusOutlined,
  SafetyOutlined,
  HeartOutlined,
  MailOutlined,
  GroupOutlined
} from "@ant-design/icons";

import itemsComponents from "../../../../../components/items";

import images from "../../../../../assets/images";
import global from "../../../../../config/global";
import common from "../../../../../utils/common";

const Utilities = (props) => {
  const {
    DocLink
  } = itemsComponents;
  const {
    className
  } = props
  const { t } = useTranslation();

  const plan = useSelector((state) => state.payment.plan);
  const locale = useSelector((state) => state.system.locale);
  const isMobile = useSelector((state) => state.system.isMobile);

  const [collapseKeys, setCollapseKeys] = useState(['1'])

  const planTypes = global.constants.PLANS;

  const buyPremium = (plan = planTypes.PREMIUM) => {
    global.navigate(global.keys.MANAGE_PLANS, {}, {plan: plan})
  }

  const utilities = useMemo(() => {
    const isFreePlan = common.isFreePlan();
    const isFamilyPlan = common.isFamilyPlan();
    return [
      {
        i18nKey: 'feat1',
        img: images.Feat1Img,
        hidden: !isFreePlan,
        docKey: 'UPGRADING_TO_PREMIUM_PLAN',
        button: {
          icon: <ThunderboltOutlined />,
          action: () => buyPremium()
        }
      },
      {
        i18nKey: 'feat2',
        img: images.Feat2Img,
        active: !isFreePlan,
        docKey: 'SHARE_ITEMS',
        button: {
          icon: <ShareAltOutlined />,
          action: () => isFreePlan ? buyPremium() : global.navigate(global.keys.MY_SHARED_ITEMS)
        }
      },
      {
        i18nKey: 'feat3',
        img: images.Feat3Img,
        active: !isFreePlan,
        docKey: 'SET_UP_EMERGENCY_ACCESS',
        button: {
          icon: <PlusOutlined />,
          action: () => isFreePlan ? buyPremium() : global.navigate(global.keys.SETTINGS_SECURITY, {}, {emergency_access: true})
        }
      },
      {
        i18nKey: 'feat4',
        img: images.Feat4Img,
        active: !isFreePlan,
        docKey: 'DATA_BREACH_SCANNER',
        button: {
          icon: <SafetyOutlined />,
          action: () => isFreePlan ? buyPremium() : global.navigate(global.keys.BREACH_SCANNER)
        }
      },
      {
        i18nKey: 'feat5',
        img: images.Feat5Img,
        active: !isFreePlan,
        docKey: 'PASSWORD_HEALTH_CHECKER',
        button: {
          icon: <HeartOutlined />,
          action: () => isFreePlan ? buyPremium() : global.navigate(global.keys.PASSWORD_HEALTH)
        }
      },
      {
        i18nKey: 'feat6',
        img: images.Feat6Img,
        active: !isFreePlan,
        docKey: 'PRIVATE_EMAIL_FOR_PREMIUM',
        button: {
          icon: <MailOutlined />,
          action: () => isFreePlan ? buyPremium() : global.navigate(global.keys.PRIVATE_EMAIL)
        }
      },
      {
        i18nKey: 'feat7',
        img: images.Feat7Img,
        hidden: !isFreePlan,
        docKey: '',
        button: {
          icon: <ThunderboltOutlined />,
          action: () => isFreePlan ? buyPremium() : {}
        }
      },
      {
        i18nKey: 'feat8',
        img: images.Feat8Img,
        active: isFamilyPlan,
        label: 'FAMILY',
        docKey: 'UPGRADING_TO_FAMILY_PLAN',
        button: {
          icon: <GroupOutlined />,
          action: () => !isFamilyPlan ? buyPremium(planTypes.FAMILY) : global.navigate(global.keys.SETTINGS_MEMBERS)
        }
      },
    ].filter((item) => !item.hidden)
  }, [plan, locale])

  const CollapseHeader = () => {
    return <p className="font-semibold">{t('plans_billing.utilities.title')}</p>
  }

  const CollapseBody = () => {
    return <div>
      {
        utilities.map((item, index) => <div key={index}>
          {
            index > 0 && <Divider />
          }
          <div className="flex flex-wrap items-center">
            <Image
              src={item.img}
              preview={false}
            />
            <div className="flex flex-wrap items-center justify-between flex-1 mx-4">
              <div className="flex-1">
                <div className="flex">
                  <p className="font-semibold">{t(`plans_billing.utilities.${item.i18nKey}.title`)}</p>
                  {
                    !!item.label && <div
                      className="text-white font-semibold px-3 bg-black rounded ml-2"
                    >
                      {item.label}
                    </div>
                  }
                </div>
                <p className="text-black-500 mt-2">
                  {t(`plans_billing.utilities.${item.i18nKey}.desc`)}
                </p>
                <DocLink
                  className="mt-2 font-semibold"
                  title={t(`plans_billing.utilities.${item.i18nKey}.learn_more`)}
                  docKey={item.docKey}
                />
                {
                  isMobile && <Button
                    className="mt-4"
                    type="primary"
                    ghost
                    icon={item.button.icon}
                    onClick={() => item.button.action()}
                  >
                    {t(`plans_billing.utilities.${item.i18nKey}.btn_text`)}
                  </Button>
                }
              </div>
              {
                !isMobile && <Button
                  type="primary"
                  ghost
                  icon={item.button.icon}
                  onClick={() => item.button.action()}
                >
                  {t(`plans_billing.utilities.${item.i18nKey}.btn_text`)}
                </Button>
              }
              
            </div>
          </div>
          
        </div>)
      }
    </div>
  }

  return (
    <div className={`utilities ${className}`}>
      <Collapse
        activeKey={collapseKeys}
        expandIconPosition={'end'}
        onChange={setCollapseKeys}
      >
        <Collapse.Panel
          key="1"
          header={<CollapseHeader/>}
        >
          <CollapseBody />
        </Collapse.Panel>
      </Collapse>
    </div>
  );
}

export default Utilities;